<div class="track-container">
  <div class="track-topbar">
    <div class="topbar-left">
      <h1>
        Track Item
        <small>Where it is now, and every movement</small>
      </h1>
      <div class="breadcrumb">
        <i class="bi bi-house-door"></i>
        <a routerLink="/dashboard">Home</a> /
        <a routerLink="/item-master">Inventory</a> /
        <span class="current">Track Item</span>
        <span class="separator">|</span>
        <span class="time"><i class="bi bi-clock"></i> {{ timestamp() }}</span>
      </div>
    </div>
  </div>

  <div class="picker-card">
    <div class="form-row">
      <div class="form-group grow">
        <label>Item</label>
        <input
          type="text"
          placeholder="Search by code, name, or serial number…"
          [ngModel]="itemSearch()"
          (ngModelChange)="itemSearch.set($event)"
        />
        @if (itemSearch().trim()) {
          <div class="suggest-list">
            @for (item of filteredCatalog(); track item.id) {
              <button type="button" class="suggest-row" (click)="pickCatalogItem(item)">
                <span class="suggest-code">{{ item.code }}</span>
                <span>{{ item.name }}</span>
                @if (matchedSerial(item); as serial) {
                  <span class="suggest-serial">{{ serial }}</span>
                }
              </button>
            } @empty {
              <div class="suggest-empty">No matching items or serial numbers.</div>
            }
          </div>
        }
        <select [ngModel]="selectedItemId()" (ngModelChange)="onSelectItem($event)">
          <option value="">Select an item</option>
          @for (item of selectableCatalog(); track item.id) {
            <option [value]="item.id">{{ item.code }} — {{ item.name }}{{ matchedSerial(item) ? ' — ' + matchedSerial(item) : '' }}</option>
          }
        </select>
      </div>
      <div class="form-group">
        <label>From</label>
        <input type="date" [ngModel]="startDate()" (ngModelChange)="startDate.set($event)" />
      </div>
      <div class="form-group">
        <label>To</label>
        <input type="date" [ngModel]="endDate()" (ngModelChange)="endDate.set($event)" />
      </div>
      <div class="form-group apply-group">
        <label>&nbsp;</label>
        <button class="btn btn-primary" type="button" [disabled]="loading() || !selectedItemId()" (click)="loadHistory()">
          <i class="bi bi-search"></i> {{ loading() ? 'Loading…' : 'Show trail' }}
        </button>
      </div>
    </div>
  </div>

  @if (error()) {
    <div class="banner error">{{ error() }}</div>
  }

  @if (!selectedItemId() && !history()) {
    <div class="empty-state">
      <i class="bi bi-geo-alt"></i>
      <p>Select an item to see on-hand, reserved, who has it, and the full movement trail.</p>
    </div>
  }

  @if (history(); as view) {
    @if (view.header; as header) {
      <div class="header-card">
        <div class="header-main">
          <div class="item-code">{{ header.code || selectedItem()?.code }}</div>
          <h2>{{ header.name || selectedItem()?.name || 'Item' }}</h2>
          <div class="header-meta">
            <span>{{ header.category || selectedItem()?.category || '—' }}</span>
            <span>{{ header.unit || selectedItem()?.unit }}</span>
            @if (header.isRentable) {
              <span class="pill rentable">Rentable</span>
            } @else {
              <span class="pill consumable">Consumable</span>
            }
          </div>
        </div>
        <div class="kpi-row">
          <div class="kpi">
            <span class="kpi-label">On hand</span>
            <span class="kpi-value">{{ header.totalOnHand }}</span>
          </div>
          <div class="kpi">
            <span class="kpi-label">Reserved</span>
            <span class="kpi-value">{{ header.totalReserved }}</span>
          </div>
          <div class="kpi">
            <span class="kpi-label">Available</span>
            <span class="kpi-value accent">{{ header.totalAvailable }}</span>
          </div>
          <div class="kpi">
            <span class="kpi-label">Locations</span>
            <span class="kpi-value">{{ header.locationCount || view.locations.length }}</span>
          </div>
          <div class="kpi">
            <span class="kpi-label">Last movement</span>
            <span class="kpi-value small">{{ header.lastMovementDate ? formatDate(header.lastMovementDate) : '—' }}</span>
          </div>
        </div>
      </div>
    }

    <div class="section">
      <h3><i class="bi bi-geo-alt"></i> Where it is now</h3>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Branch</th>
              <th>Store location</th>
              <th>On hand</th>
              <th>Reserved</th>
              <th>Available</th>
            </tr>
          </thead>
          <tbody>
            @for (loc of view.locations; track loc.stockBalanceId || loc.locationId) {
              <tr>
                <td>{{ loc.branchName || '—' }}</td>
                <td>{{ loc.locationName || '—' }}</td>
                <td>{{ loc.onHand }}</td>
                <td>{{ loc.reserved }}</td>
                <td class="avail">{{ loc.available }}</td>
              </tr>
            } @empty {
              <tr>
                <td colspan="5" class="empty-cell">No stock balances at this branch yet. Receive stock first.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>

    <div class="section">
      <h3><i class="bi bi-clock-history"></i> Movement trail</h3>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>What</th>
              <th>Qty</th>
              <th>Who / where</th>
              <th>Place</th>
              <th>Reference</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            @for (event of view.timeline; track $index) {
              <tr>
                <td>{{ formatWhen(event.eventDate) }}</td>
                <td>
                  <span class="event-pill" [class]="directionClass(event.direction)">{{ event.eventType }}</span>
                </td>
                <td>{{ event.quantity }}</td>
                <td>
                  <div>{{ event.party || '—' }}</div>
                  @if (event.contactPhone) {
                    <small>{{ event.contactPhone }}</small>
                  }
                </td>
                <td>{{ placeLabel(event) }}</td>
                <td><span class="ref">{{ event.reference || '—' }}</span></td>
                <td>{{ event.status || '—' }}</td>
              </tr>
            } @empty {
              <tr>
                <td colspan="7" class="empty-cell">No movements in this date range.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>

    @if (view.assets.length) {
      <div class="section">
        <h3><i class="bi bi-upc-scan"></i> Serialized units</h3>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Serial</th>
                <th>Status</th>
                <th>Condition</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              @for (asset of view.assets; track asset.id) {
                <tr>
                  <td>{{ asset.assetNumber }} — {{ asset.name }}</td>
                  <td>{{ asset.serialNumber || '—' }}</td>
                  <td>{{ asset.status || '—' }}</td>
                  <td>{{ asset.condition || '—' }}</td>
                  <td>{{ asset.locationName || asset.branchName || '—' }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    }
  }
</div>
